import React, { useEffect, useState, useRef } from "react";
import MatRequestVM from "../viewmodel/MatRequestVM";
import MCTTable from "./MCTTable";
import { useNavigate } from "react-router-dom";
import EmailRequestView from "./EmailRequestView";
import Tooltip from "@mui/material/Tooltip";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import {
  STATUS_APPROVED,
  STATUS_REJECTED,
  STATUS_IN_PROGRESS,
} from "../../../material_creation/assets/Strings";
import MaterialCreationLoader from "./MaterialCreationLoader";
import MCTStatusCards from "./MCTStatusCards";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
// ─────────────────────────────────────────────────────────────────────────────
// UTIL: Convert Unix ms timestamp → "DD-MM-YYYY"
// DB stores dates as e.g. "1749688739000" (ms) or "NaN" — handle both safely
// ─────────────────────────────────────────────────────────────────────────────
const normalizeStatusKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");


const STATUS_KEYWORD_MAP = [
  { keyword: "approv", canonical: "Approved" },
  { keyword: "reject", canonical: "Rejected" },
  { keyword: "hold", canonical: "On Hold" },
  { keyword: "progress", canonical: "In Progress" },
  { keyword: "pending", canonical: "Pending" },
  { keyword: "validation", canonical: "Validation Failed" },
  { keyword: "test", canonical: "Testing" },
];

const ALLOWED_STATUSES = [
  "In Progress",
  "On Hold",
  "Pending",
  "Approved",
  "Rejected",
  "Validation Failed",
  "Testing"
];

const resolveCanonicalStatus = (rawStatus) => {
  const normalized = normalizeStatusKey(rawStatus);
  const exact = ALLOWED_STATUSES.find(
    (s) => normalizeStatusKey(s) === normalized,
);
  if (exact) return exact;
  const fallback = STATUS_KEYWORD_MAP.find(({ keyword }) =>
    normalized.includes(keyword),
  );
  return fallback ? fallback.canonical : null;
};

const formatDate = (raw) => {
  if (!raw || raw === "NaN" || isNaN(Number(raw))) return "—";
  const date = new Date(Number(raw));
  if (isNaN(date.getTime())) return "—";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};
void formatDate;

const MatRequestView = () => {
  const {
    handleFileChoose,
    handleUpload,
    isTableVisible,
    downloadExportFile,
    handleStatusChange,
    filteredList,
    isLoading,
    handleRequestSampleUpload,
    setIsLoading,
    isUploadSubmitting,
    mctData,
  } = MatRequestVM();

  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");

  const [selectedOperation, setSelectedOperation] = useState("");
  const [selectedPlantCode, setSelectedPlantCode] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateData, setDuplicateData] = useState(null);

  const [showFileActions, setShowFileActions] = useState(true);
  const [darkMode, setDarkMode] = useState(false);

  const [lastRequestRefresh, setLastRequestRefresh] = useState(null);
  const [statusColHeight, setStatusColHeight] = useState(null);
  const statusColRef = useRef(null);

  useEffect(() => {
    const el = statusColRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setStatusColHeight(entry.contentRect.height);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // ── Load Lato font once ──────────────────────────────────────────────────
  useEffect(() => {
    const fontId = "lato-font-link";
    if (!document.getElementById(fontId)) {
      const link = document.createElement("link");
      link.id = fontId;
      link.href =
        "https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
  }, []);

  const outerTheme = useTheme();
  const localTheme = createTheme(outerTheme, {
    typography: {
      ...outerTheme.typography,
      fontFamily: "Lato, sans-serif",
    },
  });

  // ── ALLOWED STATUS FILTER ────────────────────────────────────────────────
  const allowedStatuses = [
    "In Progress",
    "On Hold",
    "Pending",
    "Approved",
    "Rejected",
    "Validation Failed",
    "Testing"
  ];


  const baseFiltered = mctData
    .map((item) => {
      const canonical = resolveCanonicalStatus(item.status);
      return canonical ? { ...item, status: canonical } : null;
    })
    .filter(Boolean);


    





  const uniqueOperations = [
    ...new Set(baseFiltered.map((i) => i.type_of_request)),
  ];
  const uniquePlantCodes = [...new Set(baseFiltered.map((i) => i.plant_id))];

  
  const uniqueStatus = [...new Set(baseFiltered.map((i) => i.status))];
  const cardFilteredData = baseFiltered.filter(
    (item) =>
      (!selectedOperation || item.type_of_request === selectedOperation) &&
      (!selectedPlantCode || item.plant_id === selectedPlantCode),
  );

  // const filteredByStatus = baseFiltered.filter(
  //     (item) =>
  //       (!selectedOperation || item.type_of_request === selectedOperation) &&
  //       (!selectedPlantCode || item.plant_id === selectedPlantCode) &&
  //       (!selectedStatus || item.status === selectedStatus),
  //   )
  //   .sort((a, b) => {
  //     if (a.status === "Pending" && b.status !== "Pending") return -1;
  //     if (a.status !== "Pending" && b.status === "Pending") return 1;
  //     return 0;
  //   });

  // const statusCounts = allowedStatuses.map((status) => ({
  //   status,
  //   count: filteredByStatus.filter((item) => item.status === status).length,
  // }));

  // ── Track last refresh whenever data changes ─────────────────────────────
  
  // Statuses visible in the table by default (before the user picks a
// specific status from the "search" dropdown)
const DEFAULT_VISIBLE_STATUSES = ["On Hold", "Pending" , "Validation Failed"];

// Operation/Plant filtered set, independent of status — used to compute
// accurate counts on every status card (including ones hidden by default)
const filteredByOpPlant = baseFiltered.filter(
  (item) =>
    (!selectedOperation || item.type_of_request === selectedOperation) &&
    (!selectedPlantCode || item.plant_id === selectedPlantCode),
);

const filteredByStatus = filteredByOpPlant
  .filter((item) =>
    selectedStatus
      ? item.status === selectedStatus
      : DEFAULT_VISIBLE_STATUSES.includes(item.status),
  )
  .sort((a, b) => {
    if (a.status === "Pending" && b.status !== "Pending") return -1;
    if (a.status !== "Pending" && b.status === "Pending") return 1;
    return 0;
  });

const statusCounts = allowedStatuses.map((status) => ({
  status,
  count: filteredByOpPlant.filter((item) => item.status === status).length,
}));
  useEffect(() => {
    if (mctData.length > 0) {
      setLastRequestRefresh(new Date());
    }
  }, [mctData]);

  // ── LOG 1: Raw filteredList on every data change ─────────────────────────
  useEffect(() => {
    if (filteredList.length > 0) {
      const statusMap = {};
      filteredList.forEach((r) => {
        statusMap[r.status] = (statusMap[r.status] || 0) + 1;
      });

      const typeMap = {};
      filteredList.forEach((r) => {
        typeMap[r.type_of_request] = (typeMap[r.type_of_request] || 0) + 1;
      });
    }
  }, [filteredList]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredList]);

  useEffect(() => {}, [isTableVisible]);

  useEffect(() => {}, [isLoading]);

  // ── WRAPPED HANDLERS ─────────────────────────────────────────────────────
  const handleStatusChangeWithLog = (field, value) => {
    // handleStatusChange(field, value);
  };

  const handleFileChooseWithLog = (file) => {
    if (!file) {
      setSelectedFile(null);
      setSelectedFileName("");
      return;
    }

    setSelectedFile(file);
    setSelectedFileName(file.name);
    handleFileChoose(file);
  };

  const handleUploadWithLog = async () => {
    const result = await handleUpload();

    setSelectedFile(null);
    setSelectedFileName("");
    handleFileChoose(null);
    if (fileInputRef.current) fileInputRef.current.value = "";

    if (!result) return;

    if (result.type === "duplicate") {
      setDuplicateData(result);
      setDuplicateDialogOpen(true);
    } else if (result.type === "success") {
      setUploadResult(result);
      setResultDialogOpen(true);
    }
  };

  const downloadExportFileWithLog = async () => {
    try {
      setIsDownloadLoading(true);
      setIsLoading(true);
      await downloadExportFile();
    } finally {
      setIsDownloadLoading(false);
      setIsLoading(false);
    }
  };

  const handleRequestSampleUploadWithLog = async () => {
    try {
      setIsDownloadLoading(true);
      setIsLoading(true);
      await handleRequestSampleUpload();
    } finally {
      setIsDownloadLoading(false);
      setIsLoading(false);
    }
  };

  const renderSelect = (id, label, options, value, onChange) => (
    <FormControl fullWidth size="small" sx={{ minWidth: 160 }}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        value={value || ""}
        label={label}
        onChange={(event) => onChange(event.target.value)}
        sx={{ backgroundColor: "#fff" }}
      >
        <MenuItem value="">All</MenuItem>
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const cHeaderData = [
    "Material No",
    "Date",
    "Description",
    renderSelect(
      "dd_operations",
      "Operations",
      uniqueOperations,
      selectedOperation,
      (v) => {
        setSelectedOperation(v);
      },
    ),
    renderSelect(
      "dd_plant_code",
      "Plant Code",
      uniquePlantCodes,
      selectedPlantCode,
      (v) => {
        setSelectedPlantCode(v);
      },
    ),
    renderSelect("dd_status", "Status", uniqueStatus, selectedStatus, (v) => {
      setSelectedStatus(v);
    }),
    "Request Status ",
    "Reason ",
    "Rejected Comments ",
    "MDM NAME ",
  ];

  const cRowData = [
    { type: "T", value: "material_number" },
    { type: "T", value: "request_date" },
    { type: "T", value: "description" },
    { type: "T", value: "type_of_request" },
    { type: "T", value: "plant_id" },
    { type: "T", value: "status" },
    {
      type: "B",
      value: "Start",
      isDisable: (actualRow) =>
        actualRow.status === STATUS_APPROVED ||
        actualRow.status === STATUS_REJECTED ||
        actualRow.status === STATUS_IN_PROGRESS,
      tooltipText: "Click to Start the Progress Page",
      actionV: (data) => {
        navigate(`/material-creation/process/${String(data.id)}`);
      },
    },
    { type: "T", value: "reason" },
    { type: "T", value: "rejected_request_comments" },
    { type: "T", value: "mdm_name" },
  ];

  const colors = {
    pageBg: darkMode ? "#0f172a" : "#f8fafc",
    paperBg: darkMode ? "#1e293b" : "#ffffff",
    text: darkMode ? "#f8fafc" : "#0f172a",
    subText: darkMode ? "#cbd5e1" : "#64748b",
    border: darkMode ? "#334155" : "#dbe3ef",
  };

  return (
    <ThemeProvider theme={localTheme}>
      <>
        <MaterialCreationLoader
          open={isLoading}
          variant={isDownloadLoading ? "download" : "processing"}
          title={
            isDownloadLoading
              ? "Preparing request file"
              : "Updating material request"
          }
          message={
            isDownloadLoading
              ? "Generating the Excel file and starting the download."
              : "Uploading or syncing the latest request data."
          }
        />

        <Box
          sx={{
            margin: "6em 0.75em 1em",
            backgroundColor: colors.pageBg,
            minHeight: "100vh",
            p: 1,
            borderRadius: "12px",
            transition: "all .3s ease",
          }}
        >
          {/* ── Header bar (chips + Email + Download) ───────────────────── */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: "8px",
              border: "1px solid #dbe3ef",
              overflow: "hidden",
              boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
              backgroundColor: "#ffffff",
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 2.5,
                background:
                  "linear-gradient(135deg, #3CB043 100%, #3CB043 100%)",
                color: "#ffffff",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={`${baseFiltered.length} Total requests`}
                    size="medium"
                    sx={{
                      color: "#ffffff",
                      backgroundColor: "rgba(255,255,255,0.16)",
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={`${uniquePlantCodes.length} Unique Plants`}
                    size="medium"
                    sx={{
                      color: "#ffffff",
                      backgroundColor: "rgba(255,255,255,0.16)",
                      fontWeight: 700,
                    }}
                  />
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent={{ xs: "flex-start", lg: "flex-end" }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <EmailRequestView />

                  <Tooltip
                    title="Download Material Request Data"
                    arrow
                    placement="bottom"
                  >
                    <Button
                      variant="contained"
                      onClick={downloadExportFileWithLog}
                      aria-label="Download Requests"
                      sx={{
                        minWidth: 0,
                        borderRadius: "8px",
                        p: 1.2,
                        backgroundColor: "#042560",
                        boxShadow: "none",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "#031c4a",
                          transform: "translateY(-2px)",
                          boxShadow: "none",
                        },
                      }}
                    >
                      <CloudDownloadOutlinedIcon
                        sx={{ fontSize: 20, color: "#ffffff" }}
                      />
                    </Button>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>
          </Paper>

          {/* ── Sidebar (left) + Status cards & Table (right) ───────────── */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 2,
              alignItems: "flex-start",
              flexDirection: { xs: "column", md: "row" },
              background: "#ffffff",
            }}
          >
            {showFileActions  && (
              <Box
                sx={{
                  width: {
                    xs: "100%",
                    md: "calc((100% - 48px) / 4)",
                    lg: "calc((100% - 80px) / 6)",
                  },
                  flexShrink: 0,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: "14px",
                    border: "1px solid #e2e8f0",
                    overflow: "hidden",
                    background: "#ffffff",
                    boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
                    width: "190px",
                    height: statusColHeight ? `${statusColHeight}px` : "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mx: -2.5,
                      mt: -2.5,
                      mb: 1,
                      px: 2.5,
                      py: 1.25,
                      background:
                        "linear-gradient(135deg, rgb(60, 176, 67) 100%, rgb(60, 176, 67) 100%)",
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <UploadFileOutlinedIcon
                        sx={{ fontSize: 18, color: "#ffffff" }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        color: "#ffffff",
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      File Actions
                    </Typography>
                  </Box>
                  {/* ── Choose File row ── */}
                  <Box
                    component="label"
                    htmlFor="excel-upload-input"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minHeight: 31,
                      cursor: "pointer",
                      transition: "background-color 0.15s ease",
                      "&:hover": { backgroundColor: "#f8fafc" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "7px",
                        backgroundColor: "#eff6ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <InsertDriveFileOutlinedIcon
                        sx={{ fontSize: 16, color: "#2563eb" }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}
                      >
                        Choose File
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: selectedFileName ? "#16a34a" : "#64748b",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {selectedFileName || ".xlsx, .xls, .csv"}
                      </Typography>
                    </Box>

                    {selectedFileName && (
                      <Box
                        component="span"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedFile(null);
                          setSelectedFileName("");
                          handleFileChoose(null);
                          if (fileInputRef.current)
                            fileInputRef.current.value = "";
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          backgroundColor: "#fee2e2",
                          color: "#ef4444",
                          fontSize: 10,
                          fontWeight: 800,
                          cursor: "pointer",
                          flexShrink: 0,
                          "&:hover": {
                            backgroundColor: "#ef4444",
                            color: "#ffffff",
                          },
                        }}
                      >
                        ✕
                      </Box>
                    )}

                    <input
                      id="excel-upload-input"
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      hidden
                      onClick={(e) => (e.target.value = null)}
                      onChange={(e) =>
                        handleFileChooseWithLog(e.target.files?.[0])
                      }
                    />
                  </Box>

                  {/* ── Download Sample row ── */}
                  <Box
                    onClick={handleRequestSampleUploadWithLog}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minHeight: 31,
                      cursor: "pointer",
                      transition: "background-color 0.15s ease",
                      "&:hover": { backgroundColor: "#f8fafc" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "7px",
                        backgroundColor: "#ecfeff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <DownloadOutlinedIcon
                        sx={{ fontSize: 16, color: "#0891b2" }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}
                      >
                        Download Sample
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                        Sample template
                      </Typography>
                    </Box>
                  </Box>

                  {/* ── Upload File row ── */}
                  <Box
                    onClick={() => {
                      if (!selectedFile || isUploadSubmitting) return;
                      handleUploadWithLog();
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minHeight: 31,
                      cursor:
                        !selectedFile || isUploadSubmitting
                          ? "not-allowed"
                          : "pointer",
                      opacity: !selectedFile ? 0.5 : 1,
                      transition: "background-color 0.15s ease",
                      "&:hover": {
                        backgroundColor:
                          !selectedFile || isUploadSubmitting
                            ? "transparent"
                            : "#f8fafc",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "7px",
                        backgroundColor: !selectedFile ? "#f1f5f9" : "#f0fdf4",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {isUploadSubmitting ? (
                        <CircularProgress
                          size={14}
                          thickness={5}
                          sx={{ color: "#16a34a" }}
                        />
                      ) : (
                        <UploadFileOutlinedIcon
                          sx={{
                            fontSize: 16,
                            color: !selectedFile ? "#94a3b8" : "#16a34a",
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}
                      >
                        Upload File
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                        {isUploadSubmitting
                          ? "Uploading…"
                          : selectedFile
                            ? "Submit the selected file"
                            : "Select a file first"}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            )}

            {/* ── Main content (status cards + table) ── */}
            <Box
              ref={statusColRef}
              sx={{ flex: 1, minWidth: 0, width: "100%" }}
            >
              <MCTStatusCards
                statusCounts={statusCounts}
                requestData={cardFilteredData}
                selectedStatus={selectedStatus}
                onStatusClick={(status) =>
                  setSelectedStatus((current) =>
                    current === status ? "" : status,
                  )
                }
                lastUpdated={lastRequestRefresh}
              />
            </Box>
          </Box>

          {isTableVisible && (
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                p: { xs: 1.5, md: 2 },
                borderRadius: "8px",
                border: "1px solid #dbe3ef",
                boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
              }}
            >
              <MCTTable
                key="mct-main-table"
                cHeaderData={cHeaderData}
                cRowData={cRowData}
                cActualData={filteredByStatus}
                cIsPagination={true}
                cSmallSize={false}
              />
            </Paper>
          )}
        </Box>

        {/* ── Upload Result Dialog ──────────────────────────────────────── */}
        <Dialog
          open={resultDialogOpen}
          onClose={() => setResultDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "12px", overflow: "hidden" },
          }}
        >
          <DialogTitle
            sx={{
              background: uploadResult?.success
                ? "linear-gradient(135deg, #16a34a 0%, #15803d 100%)"
                : "linear-gradient(135deg, #16a34a 0%, #16a34a 100%)",
              color: "#ffffff",
              fontWeight: 800,
              fontSize: 16,
              py: 2,
            }}
          >
            {uploadResult?.success ? "✅ Upload Complete" : "❌ Upload Failed"}
          </DialogTitle>

          <DialogContent sx={{ pt: 3, pb: 1 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: "8px",
                backgroundColor: "#f8fafc",
                border: "1px solid #dbe3ef",
                mb: 2,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", pt: 1 }}
              >
                TOTAL RECORDS PROCESSED
              </Typography>
              <Typography
                sx={{ fontSize: 32, fontWeight: 900, color: "#0f172a" }}
              >
                {uploadResult?.totalProcessed ?? 0}
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  borderRadius: "8px",
                  backgroundColor: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ fontSize: 18 }}>✅</Box>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 600, color: "#15803d" }}
                  >
                    Successfully Inserted
                  </Typography>
                </Stack>
                <Typography
                  sx={{ fontSize: 20, fontWeight: 900, color: "#15803d" }}
                >
                  {uploadResult?.insertedCount ?? 0}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  borderRadius: "8px",
                  backgroundColor: "#fffbeb",
                  border: "1px solid #fde68a",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ fontSize: 18 }}>⚠️</Box>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 600, color: "#b45309" }}
                  >
                    Duplicate UUIDs Skipped
                  </Typography>
                </Stack>
                <Typography
                  sx={{ fontSize: 20, fontWeight: 900, color: "#b45309" }}
                >
                  {uploadResult?.duplicateCount ?? 0}
                </Typography>
              </Box>

              {/* <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  borderRadius: "8px",
                  backgroundColor: "#fff1f2",
                  border: "1px solid #fecdd3",
                }}
              > */}
                {/* <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ fontSize: 18 }}>❌</Box>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 600, color: "#be123c" }}
                  >
                    Rejected (Validation Errors)
                  </Typography>
                </Stack>
                <Typography
                  sx={{ fontSize: 20, fontWeight: 900, color: "#be123c" }}
                >
                  {uploadResult?.rejectedCount ?? 0}
                </Typography> */}
              {/* </Box> */}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => setResultDialogOpen(false)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "8px",
                backgroundColor: "#042560",
                "&:hover": { backgroundColor: "#031c4a" },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Duplicate Warning Dialog ──────────────────────────────────── */}
        <Dialog
          open={duplicateDialogOpen}
          onClose={() => setDuplicateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
              color: "#ffffff",
              fontWeight: 800,
              fontSize: 16,
              py: 2,
            }}
          >
            ⚠️ Duplicate Records Found
          </DialogTitle>

          <DialogContent sx={{ pt: 3, pb: 1 }}>
            <Typography sx={{ fontSize: 13, color: "#64748b", mb: 2 }}>
              {duplicateData?.message}
            </Typography>

            {duplicateData?.duplicates?.inFile?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#b45309",
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  📄 Within uploaded file (
                  {duplicateData.duplicates.inFile.length})
                </Typography>
                <Box
                  sx={{
                    border: "1px solid #fde68a",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  {duplicateData.duplicates.inFile.map((d, i) => (
                    <Box
                      key={i}
                      sx={{
                        px: 2,
                        py: 1,
                        backgroundColor: i % 2 === 0 ? "#fffbeb" : "#ffffff",
                        borderBottom:
                          i < duplicateData.duplicates.inFile.length - 1
                            ? "1px solid #fde68a"
                            : "none",
                      }}
                    >
                      <Typography sx={{ fontSize: 12, color: "#92400e" }}>
                        <strong>Row {d.rowNumber}</strong> duplicates Row{" "}
                        {d.duplicateOfRow} — Material:{" "}
                        <strong>{d.material}</strong>, Date:{" "}
                        <strong>{d.date}</strong>, Plant:{" "}
                        <strong>{d.plantCode}</strong>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {duplicateData?.duplicates?.inDatabase?.length > 0 && (
              <Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#be123c",
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  🗄️ Already in database (
                  {duplicateData.duplicates.inDatabase.length})
                </Typography>
                <Box
                  sx={{
                    border: "1px solid #fecdd3",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  {duplicateData.duplicates.inDatabase.map((d, i) => (
                    <Box
                      key={i}
                      sx={{
                        px: 2,
                        py: 1,
                        backgroundColor: i % 2 === 0 ? "#fff1f2" : "#ffffff",
                        borderBottom:
                          i < duplicateData.duplicates.inDatabase.length - 1
                            ? "1px solid #fecdd3"
                            : "none",
                      }}
                    >
                      <Typography sx={{ fontSize: 12, color: "#9f1239" }}>
                        <strong>Row {d.rowNumber}</strong> — Material:{" "}
                        <strong>{d.material}</strong>, Date:{" "}
                        <strong>{d.date}</strong>, Plant:{" "}
                        <strong>{d.plantCode}</strong>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setDuplicateDialogOpen(false)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "8px",
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </>
    </ThemeProvider>
  );
};

export default MatRequestView;
